import { useState, useContext } from "react";
import {
  Box,
  Button,
  Chip,
  createTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  Tabs,
  Tab
} from "@material-ui/core";
import { ThemeProvider } from "@material-ui/core/styles";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import WatchLaterIcon from "@material-ui/icons/WatchLater";
import HotelIcon from "@material-ui/icons/Hotel";
import AlarmOnIcon from "@material-ui/icons/AlarmOn";
import { KeyboardTimePicker } from "@material-ui/pickers";
import produce from "immer";
import moment from "moment";

import { WEEKDAYS } from "./StoreEditor";
import { UserContext } from "../../../../UserProvider";
import { useI18n } from "../../../../hooks/useI18n";

const ENDOFDAY = "23:59";

function StoreTimePicker({ value, onChange, error, label, ...props }) {
  return (
    <KeyboardTimePicker
      {...props}
      label={label}
      ampm={false}
      value={!!value ? moment(value, "HH:mm") : null}
      onChange={time =>
        onChange(time?.isValid() ? time?.format("HH:mm") : null)
      }
      disableToolbar
      variant="inline"
      format="HH:mm"
      margin="dense"
      error={error ?? !value}
      style={{ width: 120 }}
      KeyboardButtonProps={{
        size: "small"
      }}
    />
  );
}

export function ScheduleCard({ value, onChange }) {
  const defaultTheme = createTheme({
    palette: {
      primary: {
        main: "#1976d2"
      },
      secondary: {
        main: "#dc004e"
      },
      background: {
        default: "#f5f7fa"
      },
      error: {
        main: "#f44336"
      },
      success: {
        main: "#4caf50"
      }
    },
    typography: {
      fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
      h6: {
        fontWeight: 500
      },
      subtitle1: {
        fontWeight: 500
      }
    },
    overrides: {
      MuiCard: {
        root: {
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)"
        }
      },
      MuiCardHeader: {
        root: {
          padding: "16px 16px 8px 16px"
        }
      },
      MuiButton: {
        root: {
          borderRadius: 4,
          textTransform: "none",
          fontWeight: 500
        },
        contained: {
          boxShadow: "none"
        }
      },
      MuiPaper: {
        rounded: {
          borderRadius: 8
        },
        elevation1: {
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)"
        }
      },
      MuiChip: {
        root: {
          height: 24
        },
        label: {
          fontSize: 12,
          padding: "0 8px"
        }
      },
      MuiDialogTitle: {
        root: {
          padding: "16px 24px"
        }
      },
      MuiDialogContent: {
        root: {
          padding: "16px 24px"
        }
      },
      MuiDialogActions: {
        root: {
          padding: "8px 24px 16px"
        }
      },
      MuiAccordion: {
        root: {
          boxShadow: "none",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          borderRadius: "8px !important",
          marginBottom: 12,
          "&:before": {
            display: "none"
          },
          "&$expanded": {
            margin: "0 0 12px 0"
          }
        },
        expanded: {}
      },
      MuiAccordionSummary: {
        root: {
          borderRadius: 8,
          backgroundColor: "rgba(0, 0, 0, 0.02)",
          "&$expanded": {
            minHeight: 48
          }
        },
        content: {
          "&$expanded": {
            margin: "12px 0"
          }
        },
        expanded: {}
      },
      MuiAccordionDetails: {
        root: {
          padding: 16,
          flexDirection: "column"
        }
      },
      MuiTabs: {
        root: {
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
        },
        indicator: {
          height: 3
        }
      },
      MuiTab: {
        root: {
          textTransform: "none",
          minWidth: "auto",
          padding: "12px 16px",
          fontWeight: 400,
          "&$selected": {
            fontWeight: 500
          }
        },
        selected: {}
      }
    }
  });

  const user = useContext(UserContext);
  const primaryColor =
    user.tenant.configuration?.primaryColor ||
    defaultTheme.palette.primary.main;

  const customTheme = createTheme({
    ...defaultTheme,
    palette: {
      ...defaultTheme.palette,
      primary: {
        main: primaryColor
      }
    }
  });

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const fullScreen = useMediaQuery(customTheme.breakpoints.down("sm"));

  const transformInitialSchedule = initialSchedule => {
    const transformed = {};

    WEEKDAYS.forEach(day => {
      transformed[day] = undefined;
    });

    if (initialSchedule) {
      Object.keys(initialSchedule).forEach(day => {
        if (initialSchedule[day] && initialSchedule[day].length > 0) {
          transformed[day] = initialSchedule[day];
        }
      });
    }

    return transformed;
  };

  const [schedule, setSchedule] = useState(transformInitialSchedule(value));

  const DAY_STATUS = {
    OPEN_CUSTOM: "open-custom",
    OPEN_24H: "open-24h",
    CLOSED: "closed",
    NOT_SET: "not-set"
  };

  const getDayStatus = day => {
    if (!schedule[day]) return DAY_STATUS.NOT_SET;

    if (schedule[day].length === 0) return DAY_STATUS.CLOSED;

    if (
      schedule[day].some(item => item.from === "00:00" && item.to === ENDOFDAY)
    ) {
      return DAY_STATUS.OPEN_24H;
    }

    return DAY_STATUS.OPEN_CUSTOM;
  };

  const handleDayStatusChange = day => event => {
    const newStatus = event.target.value;

    setSchedule(
      produce(schedule, draft => {
        switch (newStatus) {
          case DAY_STATUS.CLOSED:
            draft[day] = [];
            break;
          case DAY_STATUS.OPEN_24H:
            draft[day] = [
              {
                from: "00:00",
                to: ENDOFDAY,
                type: "Unmanned"
              }
            ];
            break;
          case DAY_STATUS.OPEN_CUSTOM:
            if (!draft[day] || draft[day].length === 0) {
              draft[day] = [
                {
                  from: "09:00",
                  to: "17:00",
                  type: "Unmanned"
                }
              ];
            } else if (
              draft[day].some(
                item => item.from === "00:00" && item.to === ENDOFDAY
              )
            ) {
              draft[day] = draft[day].filter(
                item => !(item.from === "00:00" && item.to === ENDOFDAY)
              );
              if (draft[day].length === 0) {
                draft[day].push({
                  from: "09:00",
                  to: "17:00",
                  type: "Unmanned"
                });
              }
            }
            break;
          case DAY_STATUS.NOT_SET:
            draft[day] = undefined;
            break;
        }
      })
    );
  };

  const handleAdd = day => () =>
    setSchedule(
      produce(schedule, draft => {
        if (!draft[day]) {
          draft[day] = [];
        }
        draft[day].push({
          from: null,
          to: null,
          type: "Manned"
        });
        draft[day].sort((a, b) =>
          moment(a.from, "HH:mm").diff(moment(b.from, "HH:mm"))
        );
      })
    );

  const handleSave = () => {
    const cleanSchedule = {};

    Object.keys(schedule).forEach(day => {
      if (schedule[day] !== undefined) {
        cleanSchedule[day] = schedule[day];
      }
    });

    onChange(cleanSchedule);
    setOpen(false);
  };

  function mergeDays(accumulator, currentValue) {
    if (accumulator.length < 1) return [currentValue];
    const currentAcc = accumulator.pop();
    if (JSON.stringify(currentAcc[1]) === JSON.stringify(currentValue[1])) {
      currentAcc[0] = currentAcc[0].split(" - ")[0] + " - " + currentValue[0];
      accumulator.push(currentAcc);
    } else {
      accumulator.push(currentAcc);
      accumulator.push(currentValue);
    }
    return accumulator;
  }

  const filteredScheduleEntries = Object.entries(schedule).map(
    ([day, slots]) => {
      return [day, slots];
    }
  );

  const groupedSchedule = filteredScheduleEntries.reduce(mergeDays, []);

  const sortedWeekdays = Object.keys(schedule).sort(
    (a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b)
  );

  const renderStatusIndicator = status => {
    if (status === DAY_STATUS.CLOSED) {
      return (
        <Box
          width={8}
          height={8}
          borderRadius="50%"
          bgcolor="error.main"
          mr={1.5}
          display="inline-block"
        />
      );
    } else if (status === DAY_STATUS.OPEN_24H) {
      return (
        <Box
          width={8}
          height={8}
          borderRadius="50%"
          bgcolor="success.main"
          mr={1.5}
          display="inline-block"
        />
      );
    } else if (status === DAY_STATUS.OPEN_CUSTOM) {
      return (
        <Box
          width={8}
          height={8}
          borderRadius="50%"
          bgcolor="primary.main"
          mr={1.5}
          display="inline-block"
        />
      );
    } else if (status === DAY_STATUS.NOT_SET) {
      return (
        <Box
          width={8}
          height={8}
          borderRadius="50%"
          bgcolor="grey.400"
          mr={1.5}
          display="inline-block"
        />
      );
    }
  };

  const renderTimeSlot = item => {
    if (item.from === "00:00" && item.to === ENDOFDAY) {
      return (
        <Box display="flex" alignItems="center" width="100%" py={0.75}>
          {renderStatusIndicator(DAY_STATUS.OPEN_24H)}
          <Typography variant="body2" style={{ color: "#34495e" }}>
            Open 24 hours
          </Typography>
          <Box marginLeft="auto">
            <Chip
              label={item.type === "Unmanned" ? "Open" : "Manned"}
              size="small"
              color={item.type === "Unmanned" ? "primary" : "secondary"}
              style={{
                height: 22,
                fontSize: "0.7rem",
                fontWeight: 500,
                borderRadius: 4
              }}
            />
          </Box>
        </Box>
      );
    }

    return (
      <Box display="flex" alignItems="center" width="100%" py={0.75}>
        {renderStatusIndicator(DAY_STATUS.OPEN_CUSTOM)}
        <Typography variant="body2" style={{ color: "#34495e" }}>
          {item.from} - {item.to}
        </Typography>
        <Box marginLeft="auto">
          <Chip
            label={item.type === "Unmanned" ? "Open" : "Manned"}
            size="small"
            color={item.type === "Unmanned" ? "primary" : "secondary"}
            style={{
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 500,
              borderRadius: 4
            }}
          />
        </Box>
      </Box>
    );
  };

  const { t } = useI18n();

  return (
    <ThemeProvider theme={customTheme}>
      <Box
        bgcolor="#fff"
        borderRadius={8}
        boxShadow="0 1px 4px rgba(0,0,0,0.05)"
        border="1px solid rgba(0,0,0,0.05)"
        overflow="hidden"
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2}
          borderBottom="1px solid rgba(0,0,0,0.06)"
          bgcolor="rgba(25, 118, 210, 0.03)"
        >
          <Box display="flex" alignItems="center">
            <AccessTimeIcon
              style={{
                color: customTheme.palette.primary.main,
                marginRight: 12,
                fontSize: 20
              }}
            />
            <Typography
              variant="h6"
              style={{
                fontSize: "1rem",
                fontWeight: 500,
                color: customTheme.palette.primary.main
              }}
            >
              Opening Hours
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditIcon />}
            size="small"
            onClick={() => setOpen(true)}
            style={{
              textTransform: "none",
              borderRadius: 20,
              padding: "4px 12px",
              fontWeight: 500,
              fontSize: "0.85rem"
            }}
          >
            Edit
          </Button>
        </Box>

        <Box p={3}>
          {groupedSchedule.map(([day, items], i) => (
            <Box key={i} mb={i < groupedSchedule.length - 1 ? 2.5 : 0}>
              <Typography
                variant="subtitle1"
                gutterBottom
                style={{
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  color: "#2c3e50"
                }}
              >
                {day}
              </Typography>

              {items === undefined ? (
                <Box display="flex" alignItems="center" ml={1} py={0.5}>
                  {renderStatusIndicator(DAY_STATUS.NOT_SET)}
                  <Typography variant="body2" color="textSecondary">
                    Not configured
                  </Typography>
                </Box>
              ) : items.length === 0 ? (
                <Box display="flex" alignItems="center" ml={1} py={0.5}>
                  {renderStatusIndicator(DAY_STATUS.CLOSED)}
                  <Typography variant="body2" color="textSecondary">
                    Closed
                  </Typography>
                </Box>
              ) : (
                <Box ml={1}>
                  {items.map((item, idx) => (
                    <Box key={idx}>{renderTimeSlot(item)}</Box>
                  ))}
                </Box>
              )}

              {i < groupedSchedule.length - 1 && (
                <Divider style={{ marginTop: 16 }} />
              )}
            </Box>
          ))}
        </Box>
      </Box>

      <Dialog
        open={open}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: 8,
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <AccessTimeIcon color="primary" style={{ marginRight: 8 }} />
              <Typography variant="h6">
                {t("common.messages.editOpeningHours")}
              </Typography>
            </Box>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => {
                setOpen(false);
                setSchedule(transformInitialSchedule(value));
              }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            aria-label={t("common.messages.dayStatus")}
          >
            {sortedWeekdays.map((day, index) => (
              <Tab key={day} label={day} id={`day-tab-${index}`} />
            ))}
          </Tabs>

          {sortedWeekdays.map((day, index) => (
            <Box
              key={day}
              role="tabpanel"
              hidden={activeTab !== index}
              id={`day-tabpanel-${index}`}
              aria-labelledby={`day-tab-${index}`}
              pt={3}
            >
              {activeTab === index && (
                <Box>
                  <Paper
                    style={{
                      padding: 16,
                      marginBottom: 24,
                      backgroundColor: "rgba(0, 0, 0, 0.02)"
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      Store Status for {day}
                    </Typography>

                    <RadioGroup
                      aria-label={t("common.messages.dayStatus")}
                      name={`day-status-${day}`}
                      value={getDayStatus(day)}
                      onChange={handleDayStatusChange(day)}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <FormControlLabel
                            value={DAY_STATUS.OPEN_CUSTOM}
                            control={<Radio color="primary" />}
                            label={
                              <Box display="flex" alignItems="center">
                                <WatchLaterIcon
                                  fontSize="small"
                                  style={{
                                    marginRight: 8,
                                    color: customTheme.palette.primary.main
                                  }}
                                />
                                <Typography variant="body2">
                                  Custom Hours
                                </Typography>
                              </Box>
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControlLabel
                            value={DAY_STATUS.OPEN_24H}
                            control={<Radio color="primary" />}
                            label={
                              <Box display="flex" alignItems="center">
                                <AlarmOnIcon
                                  fontSize="small"
                                  style={{
                                    marginRight: 8,
                                    color: customTheme.palette.success.main
                                  }}
                                />
                                <Typography variant="body2">
                                  Open 24h
                                </Typography>
                              </Box>
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControlLabel
                            value={DAY_STATUS.CLOSED}
                            control={<Radio color="primary" />}
                            label={
                              <Box display="flex" alignItems="center">
                                <HotelIcon
                                  fontSize="small"
                                  style={{
                                    marginRight: 8,
                                    color: customTheme.palette.error.main
                                  }}
                                />
                                <Typography variant="body2">Closed</Typography>
                              </Box>
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControlLabel
                            value={DAY_STATUS.NOT_SET}
                            control={<Radio color="primary" />}
                            label={
                              <Box display="flex" alignItems="center">
                                <AccessTimeIcon
                                  fontSize="small"
                                  style={{
                                    marginRight: 8,
                                    color: customTheme.palette.grey[600]
                                  }}
                                />
                                <Typography variant="body2">
                                  {t("common.messages.notSet")}
                                </Typography>
                              </Box>
                            }
                          />
                        </Grid>
                      </Grid>
                    </RadioGroup>
                  </Paper>

                  {getDayStatus(day) === DAY_STATUS.OPEN_CUSTOM && (
                    <>
                      {schedule[day]?.some(
                        item => item.type === "Unmanned" && item.to !== ENDOFDAY
                      ) && (
                        <Box mt={3}>
                          <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 500, marginBottom: 16 }}
                          >
                            Opening hours
                          </Typography>

                          {schedule[day]?.map(
                            (item, i) =>
                              item.type === "Unmanned" &&
                              item.to !== ENDOFDAY && (
                                <Box
                                  key={`unmanned-${i}`}
                                  display="flex"
                                  alignItems="center"
                                  mb={2}
                                  p={2}
                                  style={{
                                    border: "1px solid rgba(0, 0, 0, 0.12)",
                                    borderRadius: 4,
                                    backgroundColor: "#fff"
                                  }}
                                >
                                  <StoreTimePicker
                                    label={t("common.formFields.startTime")}
                                    value={item.from}
                                    onChange={time =>
                                      setSchedule(
                                        produce(schedule, draft => {
                                          if (!draft[day]) {
                                            draft[day] = [];
                                          }
                                          draft[day][i].from = time;
                                        })
                                      )
                                    }
                                  />
                                  <Typography
                                    variant="body2"
                                    style={{ margin: "0 8px" }}
                                  >
                                    to
                                  </Typography>
                                  <StoreTimePicker
                                    label={t("common.formFields.endTime")}
                                    value={item.to}
                                    onChange={time =>
                                      setSchedule(
                                        produce(schedule, draft => {
                                          if (!draft[day]) {
                                            draft[day] = [];
                                          }
                                          draft[day][i].to = time;
                                        })
                                      )
                                    }
                                  />
                                </Box>
                              )
                          )}
                        </Box>
                      )}

                      <Box mt={3}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 500 }}
                          >
                            Manned hours
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={handleAdd(day)}
                            size="small"
                          >
                            Add manned hours
                          </Button>
                        </Box>

                        <Typography
                          variant="caption"
                          style={{
                            display: "block",
                            marginBottom: 16,
                            color: "rgba(0, 0, 0, 0.6)"
                          }}
                        >
                          Optional: Add manned hours if you want to show when
                          staff are present.
                        </Typography>

                        {schedule[day]?.map(
                          (item, i) =>
                            item.type === "Manned" && (
                              <Box
                                key={`manned-${i}`}
                                display="flex"
                                alignItems="center"
                                mb={2}
                                p={2}
                                style={{
                                  border: "1px solid rgba(0, 0, 0, 0.12)",
                                  borderRadius: 4,
                                  backgroundColor: "#fff"
                                }}
                              >
                                <StoreTimePicker
                                  label={t("common.formFields.startTime")}
                                  value={item.from}
                                  onChange={time =>
                                    setSchedule(
                                      produce(schedule, draft => {
                                        if (!draft[day]) {
                                          draft[day] = [];
                                        }
                                        draft[day][i].from = time;
                                      })
                                    )
                                  }
                                />
                                <Typography
                                  variant="body2"
                                  style={{ margin: "0 8px" }}
                                >
                                  to
                                </Typography>
                                <StoreTimePicker
                                  label={t("common.formFields.endTime")}
                                  value={item.to}
                                  onChange={time =>
                                    setSchedule(
                                      produce(schedule, draft => {
                                        if (!draft[day]) {
                                          draft[day] = [];
                                        }
                                        draft[day][i].to = time;
                                      })
                                    )
                                  }
                                />
                                <Box marginLeft="auto">
                                  <IconButton
                                    color="secondary"
                                    size="small"
                                    onClick={() =>
                                      setSchedule(
                                        produce(schedule, draft => {
                                          draft[day].splice(i, 1);
                                        })
                                      )
                                    }
                                  >
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                            )
                        )}
                      </Box>
                    </>
                  )}

                  {getDayStatus(day) === DAY_STATUS.OPEN_24H && (
                    <Box
                      mt={3}
                      p={3}
                      textAlign="center"
                      style={{
                        backgroundColor: "rgba(76, 175, 80, 0.08)",
                        borderRadius: 8
                      }}
                    >
                      <AlarmOnIcon
                        style={{
                          fontSize: 48,
                          color: customTheme.palette.success.main,
                          marginBottom: 16
                        }}
                      />
                      <Typography variant="h6" gutterBottom>
                        Open 24 Hours
                      </Typography>
                    </Box>
                  )}

                  {getDayStatus(day) === DAY_STATUS.CLOSED && (
                    <Box
                      mt={3}
                      p={3}
                      textAlign="center"
                      style={{
                        backgroundColor: "rgba(244, 67, 54, 0.08)",
                        borderRadius: 8
                      }}
                    >
                      <HotelIcon
                        style={{
                          fontSize: 48,
                          color: customTheme.palette.error.main,
                          marginBottom: 16
                        }}
                      />
                      <Typography variant="h6" gutterBottom>
                        Closed
                      </Typography>
                    </Box>
                  )}

                  {getDayStatus(day) === DAY_STATUS.NOT_SET && (
                    <Box
                      mt={3}
                      p={3}
                      textAlign="center"
                      style={{
                        backgroundColor: "rgba(0, 0, 0, 0.03)",
                        borderRadius: 8
                      }}
                    >
                      <AccessTimeIcon
                        style={{
                          fontSize: 48,
                          color: customTheme.palette.grey[600],
                          marginBottom: 16
                        }}
                      />
                      <Typography variant="h6" gutterBottom>
                        Not Configured
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Select a status for this day to configure hours.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => {
              setOpen(false);
              setSchedule(transformInitialSchedule(value));
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={Object.entries(schedule)
              .filter(([day, items]) => items !== undefined)
              .flatMap(([day, items]) => items)
              .some(
                item =>
                  item &&
                  !(item.from === "00:00" && item.to === ENDOFDAY) &&
                  (!item.type || !item.from || !item.to)
              )}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
